import { useCallback, useMemo, useState } from "react";
import { Skeleton, Stack, Group } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import DataWrapper from "../../../../common/components/DataWrapper/DataWrapper";
import AllPolicyStats from "../AllPolicyStats/AllPolicyStats";
import AllPolicyNav from "../AllPolicyNav/AllPolicyNav";
import AllPolicyViewToggle from "../AllPolicyNav/AllPolicyViewToggle";
import AllPolicyToolbar from "../AllPolicyToolbar/AllPolicyToolbar";
import useAllPolicyStats from "../../hooks/useAllPolicyStats";
import useAllPolicyFilters from "../../hooks/useAllPolicyFilters";
import { useAllPolicyHandlers } from "../../utils/allPolicyHandlers";
import { useAllPolicyTableConfigs } from "../../utils/allPolicyTableConfigs";
import TripPolicyDrawer from "../TripPolicyDrawer/TripPolicyDrawer";
import TravellerPolicyDrawer from "../TravellerPolicyDrawer/TravellerPolicyDrawer";
import { ALL_POLICY_SWR_CONFIG } from "../../utils/allPolicyConstants";
import classes from "../../pages/Trips/Trips.module.css";

const AllPolicySection = ({
  tripsEndpoint,
  travellersEndpoint,
  viewMode,
  onViewChange,
  showStats = true,
  showNav = false,
  allowedTabs,
  tabValue,
  onTabChange,
  isPending = false,
  pathOverrides,
  searchPlaceholder = "Search name, email, cover, operator...",
  tripsFilterOptions,
  travellersFilterOptions,
}) => {
  const useDrawerFilters = Boolean(tripsFilterOptions || travellersFilterOptions);
  const [resultCount, setResultCount] = useState(0);
  const [currentSelection, setCurrentSelection] = useState([]);
  const [tripDrawerOpen, setTripDrawerOpen] = useState(false);
  const [activeTripId, setActiveTripId] = useState(null);
  const [travellerDrawerOpen, setTravellerDrawerOpen] = useState(false);
  const [activeTravellerId, setActiveTravellerId] = useState(null);

  const filters = useAllPolicyFilters(viewMode);
  const { stats, loading: statsLoading } = useAllPolicyStats(viewMode, {
    enabled: showStats,
  });
  const isMobile = useMediaQuery("(max-width: 48em)");
  const activeFilterOptions =
    viewMode === "travellers" ? travellersFilterOptions : tripsFilterOptions;

  const handleTripRowClick = useCallback((row) => {
    if (!row?.id) return;
    setActiveTripId(row.id);
    setTripDrawerOpen(true);
  }, []);

  const handleTravellerRowClick = useCallback((row) => {
    if (!row?.id) return;
    setActiveTravellerId(row.id);
    setTravellerDrawerOpen(true);
  }, []);

  const baseHandlers = useAllPolicyHandlers(currentSelection);
  const handlers = useMemo(
    () => ({ ...baseHandlers, handleTripRowClick, handleTravellerRowClick }),
    [baseHandlers, handleTripRowClick, handleTravellerRowClick]
  );

  const { tripConfig, travellersConfig } = useAllPolicyTableConfigs(handlers, pathOverrides);

  const activeTableConfig = useMemo(() => {
    const base = viewMode === "travellers" ? travellersConfig : tripConfig;
    const config = useDrawerFilters
      ? { ...base, showFilterButton: true, toolbarPy: "md" }
      : base;
    if (!isMobile) return { ...config, toolbarRowClassName: classes.toolbarRow };

    return {
      ...config,
      toolbarNoWrap: false,
      toolbarRowClassName: classes.toolbarRow,
      searchInputWidth: "100%",
      searchInputStyles: {
        root: { flex: "1 1 100%", width: "100%", minWidth: "100%" },
        input: {
          backgroundColor: "#fff",
          padding: "8px 34px",
          borderRadius: "8px",
          border: "1px solid rgba(108, 148, 180, 0.15)",
        },
      },
    };
  }, [viewMode, travellersConfig, tripConfig, isMobile, useDrawerFilters]);

  const activeTab = showNav
    ? allowedTabs?.find((tab) => tab.value === tabValue) || allowedTabs?.[0]
    : null;

  const activeEndpoint = showNav
    ? viewMode === "travellers"
      ? activeTab?.travellersEndpoint
      : activeTab?.tripsEndpoint
    : viewMode === "travellers"
      ? travellersEndpoint
      : tripsEndpoint;

  const handleBulkExport = useCallback(() => {
    if (viewMode === "travellers") {
      handlers.handleTravellersExport();
      return;
    }
    handlers.handleTripsExport(currentSelection);
  }, [viewMode, currentSelection, handlers]);

  const toolbar = useDrawerFilters
    ? null
    : AllPolicyToolbar({
        statusFilter: filters.statusFilter,
        onStatusChange: filters.setStatusFilter,
        tripStatusFilter: filters.tripStatusFilter,
        onTripStatusChange: filters.setTripStatusFilter,
        typeFilter: filters.typeFilter,
        onTypeChange: filters.setTypeFilter,
        dateRange: filters.dateRange,
        onDateRangeChange: filters.setDateRange,
        onClearDates: filters.clearDates,
        resultCount,
        onExport: handleBulkExport,
        isMobile,
      });

  if (!activeEndpoint) return null;

  return (
    <Stack gap="sm" w="100%" className={classes.page}>
      {showStats ? (
        <AllPolicyStats stats={stats} loading={statsLoading} variant={viewMode} />
      ) : null}

      {showNav && allowedTabs?.length ? (
        <AllPolicyNav
          allowedTabs={allowedTabs}
          tabValue={tabValue}
          onTabChange={onTabChange}
        />
      ) : null}

      {isPending ? (
        <Skeleton height={600} />
      ) : (
        <>
          {useDrawerFilters ? (
            <AllPolicyViewToggle viewMode={viewMode} onViewChange={onViewChange} />
          ) : null}
          <DataWrapper
            key={`${activeEndpoint}-${viewMode}`}
            endpoint={activeEndpoint}
            config={activeTableConfig}
            filterOptions={useDrawerFilters ? activeFilterOptions : undefined}
            onSelectHandler={setCurrentSelection}
            additionalFilterQuery={useDrawerFilters ? "" : filters.additionalFilterQuery}
            toolbarLeftContent={toolbar?.toolbarLeftContent}
            toolbarRightContent={toolbar?.toolbarRightContent}
            toolbarBottomContent={
              useDrawerFilters ? undefined : (
                <Group
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  w="100%"
                  className={classes.toolbarBottomRow}
                >
                  <AllPolicyViewToggle viewMode={viewMode} onViewChange={onViewChange} />
                  {toolbar?.toolbarBottomContent}
                </Group>
              )
            }
            searchPlaceholder={
              useDrawerFilters ? "Search here..." : searchPlaceholder
            }
            onData={(payload) => setResultCount(payload?.count ?? 0)}
            swrConfig={ALL_POLICY_SWR_CONFIG}
          />
        </>
      )}

      <TripPolicyDrawer
        opened={tripDrawerOpen}
        tripId={activeTripId}
        onClose={() => setTripDrawerOpen(false)}
        onTravellerClick={handleTravellerRowClick}
      />
      <TravellerPolicyDrawer
        opened={travellerDrawerOpen}
        travellerId={activeTravellerId}
        onClose={() => setTravellerDrawerOpen(false)}
      />
    </Stack>
  );
};

export default AllPolicySection;
